import { useCallback, useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { CoreContext } from "context/CoreContext";
import { ReadAppVersion } from "services/appVersion";

export function useAutoReloadOnDeploy() {
  const location = useLocation();
  const { reloadMe } = useContext(CoreContext);
  // Compare this tab's code, not a shared value another tab may have advanced.
  const loadedVersion = useRef(process.env.REACT_APP_BUILD_VERSION || null);
  const reloadRequested = useRef(false);
  const edited = useRef(false);
  const pendingVersion = useRef(null);
  const refreshProfile = useRef(reloadMe);

  useEffect(() => { refreshProfile.current = reloadMe; }, [reloadMe]);

  const applyUpdate = useCallback(current => {
    if (!loadedVersion.current) loadedVersion.current = current;
    try { localStorage.setItem('app_version', current); } catch (error) { /* Storage is optional. */ }
    pendingVersion.current = loadedVersion.current === current ? null : current;
    if (!pendingVersion.current || edited.current || document.querySelector('[role="dialog"], [aria-modal="true"]')) return;
    try {
      if (sessionStorage.getItem('did_force_reload') === current) return;
      sessionStorage.setItem('did_force_reload', current);
    } catch (error) { /* A blocked store must not disable updates. */ }
    reloadRequested.current = true;
    window.location.reload();
  }, []);

  useEffect(() => {
    edited.current = false;
    // A pending deployment can be applied after leaving an edited page without
    // fetching version.json again on every React navigation.
    if (pendingVersion.current && !reloadRequested.current) applyUpdate(pendingVersion.current);
  }, [applyUpdate, location.pathname]);

  useEffect(() => {
    let cancelled = false;
    let checking = false;
    // Entry already validates /me in ContainerAuthenticated. Subsequent checks
    // share the deployment heartbeat and never run for individual page changes.
    let lastProfileCheck = Date.now();

    const markEdited = event => {
      // Custom MUI selections can emit React callbacks without a native change
      // event. Treat interaction with their edit controls conservatively too.
      if (event.type === 'submit' || event.target?.closest?.('input, textarea, select, [contenteditable="true"], [role="combobox"], [aria-haspopup="listbox"], [role="option"], [role="checkbox"], [role="radio"], [role="switch"]')) edited.current = true;
    };

    const check = async (checkProfile = false) => {
      if (checking || reloadRequested.current || document.visibilityState === 'hidden') return;
      checking = true;
      try {
        const current = await ReadAppVersion();
        if (cancelled) return;
        if (current) applyUpdate(current);
        if (checkProfile && !reloadRequested.current && Date.now() - lastProfileCheck >= 60000) {
          lastProfileCheck = Date.now();
          await refreshProfile.current?.({ background: true });
        }
      } catch (error) {
        // A transient background failure must not clear the existing menu.
      } finally {
        checking = false;
      }
    };

    const refreshVisible = () => {
      if (document.visibilityState !== 'hidden') check(true);
    };
    document.addEventListener('input', markEdited, true);
    document.addEventListener('change', markEdited, true);
    document.addEventListener('click', markEdited, true);
    document.addEventListener('submit', markEdited, true);
    document.addEventListener('visibilitychange', refreshVisible);
    window.addEventListener('focus', refreshVisible);
    window.addEventListener('online', refreshVisible);
    const timer = window.setInterval(() => check(true), 60000);
    check();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('input', markEdited, true);
      document.removeEventListener('change', markEdited, true);
      document.removeEventListener('click', markEdited, true);
      document.removeEventListener('submit', markEdited, true);
      document.removeEventListener('visibilitychange', refreshVisible);
      window.removeEventListener('focus', refreshVisible);
      window.removeEventListener('online', refreshVisible);
    };
  }, [applyUpdate]);
}
